// Subject options per class band, and the teacher-category / class / gender
// enums the mutual_transfer_applications CHECK constraints accept.
//
// The web form (src/app/bihar/mutual-transfer/form/page.tsx) carries its own
// copy of the subject lists with long bilingual labels for <select> menus; these
// short labels are sized for a numbered WhatsApp list. The `value`s must stay
// identical in both places — they are what lands in the DB and what matching
// compares.

export const CLASS_BANDS = ['1-5', '6-8', '9-10', '11-12'] as const;
export type ClassBand = (typeof CLASS_BANDS)[number];

/**
 * Which school type a class band belongs to. 1-5 IS primary; everything above
 * it is not.
 *
 * This used to be asked as a separate question on both the chat form and the
 * website, and teachers kept answering it inconsistently with the class they
 * had just given — a 1-5 teacher picking "Other" ends up matched against middle
 * and high schools only, which is nobody's intent. It is not a preference;
 * it follows from the class, so it is derived rather than asked.
 */
export function schoolTypeForClass(band: ClassBand | string | null | undefined): 'primary' | 'other' {
  return band === '1-5' ? 'primary' : 'other';
}

export const TEACHER_CATEGORIES = ['bpsc', 'exclusive', 'headteacher/headmaster'] as const;
export type TeacherCategory = (typeof TEACHER_CATEGORIES)[number];

export const GENDERS = ['male', 'female', 'other'] as const;
export type Gender = (typeof GENDERS)[number];

// A head teacher / headmaster has no teaching subject — the form stores this
// sentinel instead (route.ts does the same substitution).
export const SCHOOL_HEAD_SUBJECT = 'school-head';

export interface SubjectOption {
  value: string;
  /** Short "English / हिन्दी" label for a numbered chat list. */
  label: string;
}

export const SUBJECTS_BY_CLASS: Record<ClassBand, SubjectOption[]> = {
  '1-5': [
    { value: 'hindi', label: 'Hindi / हिंदी' },
    { value: 'english', label: 'English / अंग्रेज़ी' },
    { value: 'urdu', label: 'Urdu / उर्दू' },
    { value: 'bengali', label: 'Bengali / बांग्ला' },
    { value: 'mathematics', label: 'Mathematics / गणित' },
    { value: 'environmental-studies', label: 'EVS / पर्यावरण अध्ययन' },
  ],
  '6-8': [
    { value: 'hindi', label: 'Hindi / हिन्दी' },
    { value: 'english', label: 'English / अंग्रेज़ी' },
    { value: 'urdu', label: 'Urdu / उर्दू' },
    { value: 'sanskrit', label: 'Sanskrit / संस्कृत' },
    { value: 'mathematics', label: 'Mathematics / गणित' },
    { value: 'science', label: 'Science / विज्ञान' },
    { value: 'social-science', label: 'Social Science / सामाजिक विज्ञान' },
  ],
  '9-10': [
    { value: 'hindi', label: 'Hindi / हिन्दी' },
    { value: 'english', label: 'English / अंग्रेज़ी' },
    { value: 'urdu', label: 'Urdu / उर्दू' },
    { value: 'bengali', label: 'Bengali / बांग्ला' },
    { value: 'sanskrit', label: 'Sanskrit / संस्कृत' },
    { value: 'arabic', label: 'Arabic / अरबी' },
    { value: 'persian', label: 'Persian / फ़ारसी' },
    { value: 'maithili', label: 'Maithili / मैथिली' },
    { value: 'mathematics', label: 'Mathematics / गणित' },
    { value: 'science', label: 'Science / विज्ञान' },
    { value: 'social-science', label: 'Social Science / सामाजिक विज्ञान' },
    { value: 'fine-arts', label: 'Fine Arts / ललित कला' },
    { value: 'dance', label: 'Dance / नृत्य' },
    { value: 'physical-education', label: 'Physical Education / शारीरिक शिक्षा' },
    { value: 'music', label: 'Music / संगीत' },
  ],
  '11-12': [
    { value: 'hindi', label: 'Hindi / हिन्दी' },
    { value: 'urdu', label: 'Urdu / उर्दू' },
    { value: 'english', label: 'English / अंग्रेज़ी' },
    { value: 'sanskrit', label: 'Sanskrit / संस्कृत' },
    { value: 'bangla', label: 'Bangla / बांग्ला' },
    { value: 'persian', label: 'Persian / फ़ारसी' },
    { value: 'arabic', label: 'Arabic / अरबी' },
    { value: 'pali', label: 'Pali / पालि' },
    { value: 'prakrit', label: 'Prakrit / प्राकृत' },
    { value: 'mathematics', label: 'Mathematics / गणित' },
    { value: 'physics', label: 'Physics / भौतिकी' },
    { value: 'chemistry', label: 'Chemistry / रसायन' },
    { value: 'biology', label: 'Biology / जीव विज्ञान' },
    { value: 'botany', label: 'Botany / वनस्पति विज्ञान' },
    { value: 'zoology', label: 'Zoology / प्राणी विज्ञान' },
    { value: 'computer-science', label: 'Computer Science / कम्प्यूटर साइंस' },
    { value: 'multimedia-web-tech', label: 'Multimedia & Web Tech / मल्टीमीडिया' },
    { value: 'history', label: 'History / इतिहास' },
    { value: 'political-science', label: 'Political Science / राजनीति शास्त्र' },
    { value: 'geography', label: 'Geography / भूगोल' },
    { value: 'economics', label: 'Economics / अर्थशास्त्र' },
    { value: 'sociology', label: 'Sociology / समाजशास्त्र' },
    { value: 'psychology', label: 'Psychology / मनोविज्ञान' },
    { value: 'philosophy', label: 'Philosophy / दर्शनशास्त्र' },
    { value: 'home-science', label: 'Home Science / गृह विज्ञान' },
    { value: 'business-studies', label: 'Business Studies / बिजनेस स्टडीज' },
    { value: 'accountancy', label: 'Accountancy / एकाउन्टेन्सी' },
    { value: 'entrepreneurship', label: 'Entrepreneurship / इंटरप्रेनरशिप' },
    { value: 'music', label: 'Music / संगीत' },
  ],
};
